import React, { useState } from 'react';
import { Settings, ChevronDown, ChevronUp } from 'lucide-react';

interface DevelopmentControlsProps {
  onStateChange: (state: any) => void;
  currentFlowState: any;
  currentWhatsappData: any;
}

const DevelopmentControls: React.FC<DevelopmentControlsProps> = ({
  onStateChange,
  currentFlowState,
  currentWhatsappData
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const simulateState = (stateName: string) => {
    const now = new Date();
    
    switch (stateName) {
      case 'initial':
        onStateChange({
          flowState: {
            currentStep: 'initial',
            isLoading: false,
            error: null,
            success: false,
            cancelled: false
          },
          whatsappBusinessData: {
            workflow_step: 0,
            workflow_loading: false,
            message_history: []
          },
          connectionStatus: { isConnected: false },
          clearLocalStorage: true
        });
        break;

      case 'connecting':
        onStateChange({
          flowState: {
            currentStep: 'facebook_login_launched',
            isLoading: true,
            error: null,
            success: false,
            cancelled: false
          },
          whatsappBusinessData: {
            workflow_step: 0,
            workflow_loading: false
          }
        });
        break;

      case 'waiting_whatsapp':
        onStateChange({
          flowState: {
            currentStep: 'waiting_for_whatsapp_setup',
            isLoading: false,
            error: null,
            success: false,
            cancelled: false
          },
          whatsappBusinessData: {
            workflow_step: 0,
            workflow_loading: false
          }
        });
        break;

      case 'onboarding_completed':
        onStateChange({
          flowState: {
            currentStep: 'business_onboarding_completed',
            isLoading: false,
            error: null,
            success: true,
            cancelled: false
          },
          whatsappBusinessData: {
            waba_id: '123456789012345',
            business_id: '987654321098765',
            code: 'mock_auth_code_12345',
            workflow_step: 0,
            workflow_loading: false,
            message_history: []
          },
          connectionStatus: {
            isConnected: true,
            wabaId: '123456789012345',
            connectedAt: now,
            authCode: 'mock_auth_code_12345',
            userId: 'mock_user_123'
          },
          mockLocalStorage: {
            whatsapp_business_onboarding: {
              waba_id: '123456789012345',
              business_id: '987654321098765',
              code: 'mock_auth_code_12345',
              timestamp: now.toISOString()
            },
            facebook_login_callback: {
              authResponse: {
                code: 'mock_auth_code_12345',
                userID: 'mock_user_123'
              },
              status: 'connected',
              timestamp: now.toISOString()
            },
            last_updated: now.toISOString()
          }
        });
        break;

      case 'with_access_token':
        onStateChange({
          flowState: {
            currentStep: 'business_onboarding_completed',
            isLoading: false,
            error: null,
            success: true,
            cancelled: false
          },
          whatsappBusinessData: {
            waba_id: '123456789012345',
            business_id: '987654321098765',
            code: 'mock_auth_code_12345',
            access_token: 'mock_access_token_abcdef123456',
            workflow_step: 2,
            workflow_loading: false,
            message_history: []
          },
          connectionStatus: {
            isConnected: true,
            wabaId: '123456789012345',
            connectedAt: now,
            authCode: 'mock_auth_code_12345',
            userId: 'mock_user_123'
          },
          mockLocalStorage: {
            whatsapp_business_onboarding: {
              waba_id: '123456789012345',
              business_id: '987654321098765',
              code: 'mock_auth_code_12345',
              timestamp: now.toISOString()
            },
            facebook_login_callback: {
              authResponse: {
                code: 'mock_auth_code_12345',
                userID: 'mock_user_123'
              },
              status: 'connected',
              timestamp: now.toISOString()
            },
            access_token: {
              token: 'mock_access_token_abcdef123456',
              obtained_at: now.toISOString()
            },
            last_updated: now.toISOString()
          }
        });
        break;

      case 'workflow_running':
        onStateChange({
          whatsappBusinessData: {
            waba_id: '123456789012345',
            business_id: '987654321098765',
            code: 'mock_auth_code_12345',
            access_token: 'mock_access_token_abcdef123456',
            workflow_step: 3,
            workflow_loading: true,
            message_history: []
          }
        });
        break;

      case 'message_sent':
        onStateChange({
          whatsappBusinessData: {
            waba_id: '123456789012345',
            business_id: '987654321098765',
            code: 'mock_auth_code_12345',
            access_token: 'mock_access_token_abcdef123456',
            workflow_step: 5,
            workflow_loading: false,
            phone_numbers: [
              { id: '1234567890', verified_name: 'Test Business', display_phone_number: '+1234567890' }
            ],
            selected_phone_number: '1234567890',
            message_history: [
              {
                timestamp: new Date(),
                type: 'regular' as const,
                phone_number: '+1234567890',
                message_text: 'Mensaje de prueba desde Cliengo',
                response: { messages: [{ id: 'msg_123' }] }
              }
            ]
          }
        });
        break;

      case 'error_state':
        onStateChange({
          flowState: {
            currentStep: 'error',
            isLoading: false,
            error: 'Error simulado para pruebas de desarrollo',
            success: false,
            cancelled: false
          }
        });
        break;

      case 'cancelled_state':
        onStateChange({
          flowState: {
            currentStep: 'cancelled',
            isLoading: false,
            error: null,
            success: false,
            cancelled: true
          }
        });
        break;

      default:
        break;
    }
  };

  if (process.env.ENVIRONMENT === 'prod') {
    return null;
  }

  return (
    <div className="fixed top-[50px] right-4 z-50 bg-white border border-gray-300 rounded-lg shadow-lg max-w-sm">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-t-lg transition-colors"
      >
        <div className="flex items-center gap-2">
          <Settings className="w-4 h-4 text-gray-600" />
          <span className="text-sm font-medium text-gray-700">Dev Controls</span>
        </div>
        {isOpen ? (
          <ChevronUp className="w-4 h-4 text-gray-600" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-600" />
        )}
      </button>

      {isOpen && (
        <div className="p-3 space-y-2">
          <div className="text-xs text-gray-500 mb-3">
            Estado actual: <span className="font-medium">{currentFlowState.currentStep}</span>
          </div>

          <div className="space-y-1">
            <button
              onClick={() => simulateState('initial')}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded border"
            >
              🔄 Estado Inicial
            </button>

            <button
              onClick={() => simulateState('connecting')}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded border"
            >
              ⏳ Conectando...
            </button>

            <button
              onClick={() => simulateState('waiting_whatsapp')}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded border"
            >
              ⏰ Esperando WhatsApp
            </button>

            <button
              onClick={() => simulateState('onboarding_completed')}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded border"
            >
              ✅ Onboarding Completado
            </button>

            <button
              onClick={() => simulateState('with_access_token')}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded border"
            >
              🔑 Con Access Token
            </button>

            <button
              onClick={() => simulateState('workflow_running')}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded border"
            >
              🚀 Enviando Mensaje...
            </button>

            <button
              onClick={() => simulateState('message_sent')}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded border"
            >
              📱 Mensaje Enviado
            </button>

            <button
              onClick={() => simulateState('error_state')}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded border text-red-600"
            >
              ❌ Estado de Error
            </button>

            <button
              onClick={() => simulateState('cancelled_state')}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded border text-orange-600"
            >
              🚫 Estado Cancelado
            </button>
          </div>

          <div className="pt-2 border-t border-gray-200">
            <div className="text-xs text-gray-500">
              Workflow Step: {currentWhatsappData.workflow_step || 0}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DevelopmentControls;